import { ArrowLeft } from "lucide-react";

/**
 * JothidaKural shared UI kit — layout + form primitives.
 * Design: warm paper bg, B&W structure, editorial type, soft rounded,
 * orange logo (#FF4D00) as the only brand colour, black buttons/active states,
 * pastels for emphasis. CTAs always pinned to the bottom (thumb zone).
 */

// Full-height mobile shell — header / scroll / bottom-bar compose inside.
export const Screen = ({ children, testid, className = "" }) => (
  <div
    data-testid={testid}
    className={`relative mx-auto flex h-[100dvh] w-full max-w-[440px] flex-col overflow-hidden bg-[var(--jk-paper)] ${className}`}
  >
    {children}
  </div>
);

// Scrollable content region between top bar and bottom CTA.
export const ScrollArea = ({ children, className = "" }) => (
  <div className={`min-h-0 flex-1 overflow-y-auto ${className}`}>{children}</div>
);

// Bottom-pinned CTA container (thumb zone). Always visible.
export const BottomBar = ({ children, className = "" }) => (
  <div
    className={`shrink-0 border-t border-[#efece7] bg-[var(--jk-paper)] px-6 pb-7 pt-4 ${className}`}
  >
    {children}
  </div>
);

export const Logo = ({ className = "h-[18px] w-auto" }) => (
  <img src="/logo.svg" alt="JothidaKural" className={className} />
);

export const TopBar = ({ left, center, right }) => (
  <header className="grid h-14 shrink-0 grid-cols-[1fr_auto_1fr] items-center px-6">
    <div className="flex items-center justify-start">{left}</div>
    <div className="flex items-center justify-center">{center}</div>
    <div className="flex items-center justify-end">{right}</div>
  </header>
);

export const BackButton = ({ onClick, testid = "back-button" }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-label="Go back"
    className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-[#111111] transition-colors hover:bg-black/5"
  >
    <ArrowLeft size={22} strokeWidth={1.75} />
  </button>
);

export const PrimaryButton = ({ children, icon, testid, onClick, disabled = false, type = "button" }) => (
  <button
    type={type}
    data-testid={testid}
    onClick={onClick}
    disabled={disabled}
    className="flex h-[54px] w-full items-center justify-center gap-2 rounded-[16px] bg-[#111111] text-[15px] font-semibold text-white transition-all duration-150 ease-out enabled:active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-[#f2f2f2] disabled:text-[#aaaaaa]"
  >
    {children}
    {icon}
  </button>
);

export const SecondaryButton = ({ children, icon, testid, onClick, type = "button" }) => (
  <button
    type={type}
    data-testid={testid}
    onClick={onClick}
    className="flex h-[54px] w-full items-center justify-center gap-2 rounded-[16px] border border-[#111111] bg-white text-[15px] font-semibold text-[#111111] transition-all duration-150 ease-out active:scale-[0.99]"
  >
    {icon}
    {children}
  </button>
);

export const TextField = ({ label, testid, error, rightIcon, prefix, ...props }) => (
  <div className="w-full">
    {label && (
      <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[#666666]">
        {label}
      </label>
    )}
    <div className="relative flex items-center">
      {prefix && (
        <span className="pointer-events-none absolute left-4 text-[14px] font-medium text-[#666666]">
          {prefix}
        </span>
      )}
      <input
        data-testid={testid}
        className={`h-[52px] w-full rounded-[12px] border bg-[#f9f9f9] px-4 text-[14px] text-[#111111] outline-none transition-colors placeholder:text-[#aaaaaa] focus:bg-white ${
          error ? "border-[#d23b2f] focus:border-[#d23b2f]" : "border-[#e5e5e5] focus:border-[#111111]"
        } ${rightIcon ? "pr-11" : ""} ${prefix ? "pl-10" : ""}`}
        {...props}
      />
      {rightIcon && (
        <span className="pointer-events-none absolute right-3.5 text-[#aaaaaa]">{rightIcon}</span>
      )}
    </div>
    {error && (
      <p className="mt-1.5 text-[12px] text-[#d23b2f]" data-testid={testid ? `${testid}-error` : undefined}>
        {error}
      </p>
    )}
  </div>
);

export const TextArea = ({ label, testid, error, maxLength, value, ...props }) => (
  <div className="w-full">
    {label && (
      <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[#666666]">
        {label}
      </label>
    )}
    <div className="relative">
      <textarea
        data-testid={testid}
        value={value}
        maxLength={maxLength}
        className={`w-full rounded-[12px] border bg-[#f9f9f9] p-4 text-[14px] leading-relaxed text-[#111111] outline-none transition-colors placeholder:text-[#aaaaaa] focus:bg-white ${
          error ? "border-[#d23b2f]" : "border-[#e5e5e5] focus:border-[#111111]"
        }`}
        {...props}
      />
      {maxLength && (
        <span className="absolute bottom-2.5 right-3 text-[11px] text-[#aaaaaa]">
          {(value || "").length}/{maxLength}
        </span>
      )}
    </div>
  </div>
);
